import { useProgressBar } from '@hooks/helpers/useProgressBar';
import styles from './styles.module.scss';
import { pageAnimation } from '@const/animation';
import { motion } from 'framer-motion';
import { setUpAppMFASteps } from '@const/progress_bar_steps';
import { useEffect, useState } from 'react';
import Button from '@components/common/button';
import Typography from '@components/common/typography';
import ProgressBar from '@components/progress_bar';
import { routes } from '@const/routes';
import GetReady from '@sections/settings/setup_app_mfa/steps/get_ready';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { apiAuthService } from '@api';
import { ETwoFAMethod, TSetupTwoFAResult } from '@xeppt/xeppt-sdk/types';
import Connect from '@sections/settings/setup_app_mfa/steps/connect';
import { useLocales } from '@hooks/helpers/useLocales';
import Verify from '@sections/settings/setup_app_mfa/steps/verify';
import BackupCode from '@sections/settings/setup_app_mfa/steps/backup_code';
import Success from '@sections/settings/setup_app_mfa/steps/success';
import { useNavigate } from 'react-router-dom';
import useModalContext from '@hooks/context/useModalContext';
import { useUserContext } from '@hooks/context/useUserContext';
import { useNavigationContext } from '@hooks/context/useNavigationContext';

const SetUpMFALayout = () => {
    const navigate = useNavigate();
    const { requestErrorLocale, submitLocale } = useLocales();
    const { handleBack } = useNavigationContext();
    const { onClose } = useModalContext();
    const { refetchUser } = useUserContext();
    const [MFAData, setMFAData] = useState<TSetupTwoFAResult>();
    const [recoveryCode, setRecoveryCode] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const { availableStep, prevStep, nextStep, currentStep, changeStep } =
        useProgressBar(setUpAppMFASteps);

    const { handleRequest: handleStartMFA } = useApiMutation({
        method: () => apiAuthService.setupTwoFA(ETwoFAMethod.TOTP),
        onSuccess: (data) => {
            setMFAData(data);
            nextStep();
        },
        onError: requestErrorLocale
    });

    useEffect(() => {
        onClose();
    }, []);

    const { handleRequest: handleVerifyMFA, isLoading: isVerifyLoading } = useApiMutation({
        method: () =>
            apiAuthService.confirmTwoFA({ token: MFAData?.token || '', code: verificationCode }),
        onSuccess: (data) => {
            nextStep();
            if (data) {
                setRecoveryCode(data);
            }
            refetchUser();
        },
        onError: requestErrorLocale
    });

    const renderContent = () => {
        switch (currentStep?.id) {
            case '1':
                return <GetReady onSubmit={() => handleStartMFA(undefined)} />;
            case '2':
                return <Connect onPrev={prevStep} onSubmit={nextStep} data={MFAData} />;
            case '3':
                return (
                    <Verify
                        onPrev={prevStep}
                        onSubmit={() => handleVerifyMFA(undefined)}
                        onChange={(val) => setVerificationCode(val)}
                        value={verificationCode}
                        isLoading={isVerifyLoading}
                    />
                );
            case '4':
                return (
                    <BackupCode onPrev={prevStep} onSubmit={nextStep} recoveryCode={recoveryCode} />
                );
            case '5':
                return <Success onSubmit={() => navigate(routes.settings_security)} />;
        }
    };

    useEffect(() => {
        scrollTo(0, 0);
    }, [currentStep]);

    return (
        <motion.div {...pageAnimation} className={styles.main_wrapper}>
            <div className={styles.header}>
                <Button
                    className={styles.back}
                    leftIcon="arrow_left"
                    variant="text-gray"
                    onClick={() => handleBack(routes.settings_security)}>
                    {submitLocale('back')}
                </Button>
                <Typography variant="h4">Set up authentication app</Typography>
            </div>
            <div className={styles.content_wrapper}>
                <ProgressBar
                    steps={setUpAppMFASteps}
                    availableStep={availableStep}
                    currentStep={currentStep}
                    className={styles.progress}
                    changeStep={changeStep}
                />
                <div className={styles.body_wrapper}>{renderContent()}</div>
            </div>
        </motion.div>
    );
};

export default SetUpMFALayout;
